import { useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useQuery } from '../lib/useQuery.js';
import { useAuth } from '../lib/auth.js';
import { Page } from '../app/AppShell.js';
import { Modal } from '../components/Modal.js';
import { useToast } from '../components/Toast.js';
import { ShareWithDemoAdmins } from '../components/ShareWithDemoAdmins.js';
import { OnlineDot, Loading, ErrorNote, EmptyState } from '../components/ui.js';
import type { Display, DisplayGroup, Layout } from '../lib/types.js';

/**
 * Display groups: a named set of displays whose default layout is changed in
 * one action. Applying a layout here writes every member's default layout — the
 * same field the Display detail picker sets — and nudges them to sync.
 */
export function DisplayGroupsScreen(): ReactNode {
  const groups = useQuery(() => api.displayGroups(), []);
  const displays = useQuery(() => api.displays(), []);
  const layouts = useQuery(() => api.layouts(), []);
  const { can } = useAuth();
  const canManage = can('displays.manage');

  // `null` = closed, 'new' = create, otherwise the group being edited.
  const [editing, setEditing] = useState<DisplayGroup | 'new' | null>(null);
  // Admins share items with demo administrators, who otherwise only see what they made.
  const canShare = can('users.manage');
  const [sharing, setSharing] = useState<{ id: string; name: string } | null>(null);

  const layoutName = (id: string | null): string =>
    id === null ? '—' : (layouts.data?.find((l) => l.id === id)?.name ?? 'Unknown layout');

  return (
    <Page
      title="Display groups"
      actions={
        canManage ? (
          <button className="btn primary" onClick={() => setEditing('new')}>
            New group
          </button>
        ) : null
      }
    >
      {groups.loading && !groups.data ? <Loading what="display groups" /> : null}
      {groups.error ? <ErrorNote error={groups.error} /> : null}

      {groups.data ? (
        groups.data.length === 0 ? (
          <EmptyState>
            No display groups yet. Group screens that should show the same content, then change
            their layout together.
          </EmptyState>
        ) : (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Displays</th>
                  <th>Group layout</th>
                  {canManage ? <th>Change layout</th> : null}
                  <th />
                </tr>
              </thead>
              <tbody>
                {groups.data.map((g) => (
                  <tr key={g.id}>
                    <td>
                      {g.name}
                      {g.description ? <div className="faint small">{g.description}</div> : null}
                    </td>
                    <td className="muted small">{g.displayCount}</td>
                    <td className="muted small">{layoutName(g.layoutId)}</td>
                    {canManage ? (
                      <td>
                        <ApplyLayoutControl
                          group={g}
                          layouts={layouts.data ?? []}
                          onApplied={() => groups.refetch()}
                        />
                      </td>
                    ) : null}
                    <td>
                      {canShare ? (
                        <button className="btn small ghost" onClick={() => setSharing({ id: g.id, name: g.name })}>
                          Share
                        </button>
                      ) : null}
                      {canManage ? (
                        <button className="btn small ghost" onClick={() => setEditing(g)}>
                          Edit
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {editing !== null ? (
        <GroupModal
          group={editing === 'new' ? null : editing}
          displays={displays.data ?? []}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            groups.refetch();
          }}
        />
      ) : null}
      {sharing !== null ? (
        <ShareWithDemoAdmins type="display_group" id={sharing.id} name={sharing.name} onClose={() => setSharing(null)} />
      ) : null}
    </Page>
  );
}

function ApplyLayoutControl({
  group,
  layouts,
  onApplied,
}: {
  group: DisplayGroup;
  layouts: Layout[];
  onApplied: () => void;
}): ReactNode {
  const toast = useToast();
  const [layoutId, setLayoutId] = useState(group.layoutId ?? '');
  const [busy, setBusy] = useState(false);

  async function apply(): Promise<void> {
    const target = layoutId || null;
    const name = target === null ? 'no default layout' : `“${layouts.find((l) => l.id === target)?.name ?? target}”`;
    if (!confirm(`Set ${name} on all ${group.displayCount} display(s) in “${group.name}”?`)) return;
    setBusy(true);
    try {
      const result = await api.applyDisplayGroupLayout(group.id, target);
      toast.push(`Updated ${result.updated} display(s)`, 'success');
      onApplied();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="row">
      <select value={layoutId} disabled={busy} onChange={(e) => setLayoutId(e.target.value)}>
        <option value="">— none —</option>
        {layouts.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
            {l.status !== 'published' ? ' (draft)' : ''}
          </option>
        ))}
      </select>
      <button
        className="btn small primary"
        disabled={busy || group.displayCount === 0}
        title={group.displayCount === 0 ? 'Add displays to this group first' : undefined}
        onClick={() => void apply()}
      >
        {busy ? 'Applying…' : 'Apply'}
      </button>
    </div>
  );
}

function GroupModal({
  group,
  displays,
  onClose,
  onSaved,
}: {
  group: DisplayGroup | null;
  displays: Display[];
  onClose: () => void;
  onSaved: () => void;
}): ReactNode {
  const toast = useToast();
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [members, setMembers] = useState<Set<string>>(() => new Set(group?.displayIds ?? []));
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const visible = displays.filter((d) => d.name.toLowerCase().includes(filter.trim().toLowerCase()));

  function toggle(id: string): void {
    setMembers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function save(): Promise<void> {
    setBusy(true);
    setError(undefined);
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      displayIds: [...members],
    };
    try {
      if (group) await api.updateDisplayGroup(group.id, body);
      else await api.createDisplayGroup(body);
      toast.push(group ? 'Group saved' : 'Group created', 'success');
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? (err.fieldMessage() ?? err.message) : String(err));
      setBusy(false);
    }
  }

  async function remove(): Promise<void> {
    if (!group || !confirm(`Delete group “${group.name}”? Its displays keep their current layout.`)) return;
    setBusy(true);
    try {
      await api.deleteDisplayGroup(group.id);
      toast.push('Group deleted', 'success');
      onSaved();
    } catch (err) {
      toast.error(err);
      setBusy(false);
    }
  }

  return (
    <Modal title={group ? `Edit “${group.name}”` : 'New display group'} onClose={onClose}>
      <div className="stack">
        <label className="field">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="field">
          Description
          <input value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>

        <div className="field">
          <span>
            Displays <span className="faint small">({members.size} selected)</span>
          </span>
          {displays.length > 8 ? (
            <input placeholder="Filter displays…" value={filter} onChange={(e) => setFilter(e.target.value)} />
          ) : null}
          <div className="card" style={{ maxHeight: 280, overflowY: 'auto', padding: '4px 10px' }}>
            {displays.length === 0 ? <p className="faint small">No displays yet.</p> : null}
            {visible.map((d) => (
              <label key={d.id} className="row" style={{ padding: '4px 0', cursor: 'pointer' }}>
                <input type="checkbox" checked={members.has(d.id)} onChange={() => toggle(d.id)} />
                <OnlineDot lastSeenAt={d.lastSeenAt} />
                <span>{d.name}</span>
              </label>
            ))}
          </div>
        </div>

        {error ? <div className="form-error">{error}</div> : null}
        <div className="row between">
          {group ? (
            <button className="btn danger" disabled={busy} onClick={() => void remove()}>
              Delete
            </button>
          ) : (
            <button className="btn" onClick={onClose}>
              Cancel
            </button>
          )}
          <button className="btn primary" disabled={busy || name.trim() === ''} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
